import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Alert,
  Spinner,
} from "reactstrap";
import MetaTags from "react-meta-tags";
import FeatherIcon from "feather-icons-react";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";

// Mapping of internal action codes to clean, human-readable labels
const ACTION_LABEL_MAP = {
  // Authentication
  "auth:login_success": "Login Succeeded",
  "auth:login_failed": "Login Failed",
  "auth:logout": "User Logout",
  // User Management
  "user:create": "User Created",
  "user:update": "User Updated",
  "user:reset-password": "Password Reset",
  "user:reset_password": "Password Reset",
  "user:change-password": "Password Changed",
  "user:change_password": "Password Changed",
  // Exposed Credentials
  "credentials:reveal": "Credential Revealed",
  "credentials:reveal_denied": "Reveal Blocked (429/403)",
  "credentials:list_view": "Credentials Viewed",
  // Target Scope
  "scope:create": "Scope Authorized",
  "scope:revoke": "Scope Revoked",
  // Scans
  "scan:trigger": "Scan Triggered",
  "scan:kill": "Scan Aborted",
  "scan:retest": "Finding Retest",
  "scan:rescan": "Scope Rescan",
  // Findings
  "finding:status-change": "Status Updated",
  "finding:status_change": "Status Updated",
  "finding:assign": "Finding Assigned",
  // Exports, Reports & Auditing
  "export:asset_inventory": "Asset Inventory Export",
  "report:generate": "ASM Report Generated",
  "report:blackbox_download": "Scan Report Downloaded",
  "audit:export": "Audit Logs Exported",
  // Findings (Findings page)
  "finding:status_update": "Status Updated",
  "finding:owner_update": "Owner Changed",
  "finding:due_date_extended": "Due Date Extended",
  "finding:risk_accepted": "Risk Accepted",
  // Scans started by the platform or by the dashboard
  "scan:scheduled": "Scheduled Scan Ran",
  "scan:started": "Scan Started",
  "scan:dashboard_refresh": "Dashboard Refresh Scan",
  "greybox:request": "Grey Box Test Requested",
  // Assets
  "asset:add": "Asset Added",
  "asset:update": "Asset Edited",
  "asset:delete": "Asset Deleted",
  // Account security
  "account:register": "Account Registered",
  "account:activated": "Account Activated",
  "account:password_reset_request": "Password Reset Requested",
  "account:password_reset_done": "Password Reset Completed",
  "account:password_change": "Password Changed (Profile)",
  "account:profile_update": "Profile Updated",
  // Two-step verification
  "mfa:setup_start": "2-Step Setup Started",
  "mfa:enabled": "2-Step Verification Enabled",
  "mfa:disabled": "2-Step Verification Disabled",
  "mfa:disable_failed": "2-Step Disable Failed",
  "mfa:login_failed": "2-Step Code Failed",
  "mfa:backup_code_used": "Backup Code Used",
  "mfa:backup_codes_regenerated": "Backup Codes Regenerated",
  "mfa:reset": "2-Step Verification Reset",
  // SLA
  "sla:policy_update": "SLA Policy Updated",
  "sla:policy_reset": "SLA Policy Reset",
  // Brand protection
  "brand:case_update": "Brand Case Updated",
  "brand:evidence_export": "Brand Evidence Exported",
  "brand:watchlist_update": "Brand Watchlist Updated",
  "brand:scan_start": "Brand Scan Started",
  "brand:scan_scheduled": "Scheduled Brand Scan Ran",
  // Certificates
  "certificate:scan": "Certificate Scan Started",
  "certificate:scan_scheduled": "Scheduled Certificate Scan Ran",
  // Done by a GuardLogix administrator on this account
  "admin:alert_email_update": "GuardLogix Admin: Alert Email Changed",
  "admin:alert_email_test": "GuardLogix Admin: Test Email Sent",
  "admin:mfa_policy_update": "GuardLogix Admin: 2-Step Policy Changed",
  "admin:mfa_reset": "GuardLogix Admin: 2-Step Reset",
};

// Readable names for the part before the colon when an action has no label of its own
const AREA_LABELS = { redteam: "Red Team", mfa: "2-Step", sla: "SLA", sso: "SSO", greybox: "Grey Box" };

function getActionFriendlyLabel(action) {
  if (!action) return "Unknown Action";
  if (ACTION_LABEL_MAP[action]) return ACTION_LABEL_MAP[action];
  const parts = action.split(":");
  if (parts.length === 2) {
    const res = AREA_LABELS[parts[0]] || parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    const verb = parts[1].replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return `${res}: ${verb}`;
  }
  return action.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatRelativeTime(dateInput) {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return "";
  const now = new Date();
  const diffSec = Math.floor((now - date) / 1000);
  if (diffSec < 5) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatExactTime(dateInput) {
  if (!dateInput) return "-";
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return String(dateInput);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

const AuditLogsManagement = () => {
  const token = localStorage.getItem("token");
  const currentRole = (localStorage.getItem("role") || "").toLowerCase().trim();

  // Role gate: only tenant_admin, auditor, and super_admin may access
  const isAuthorized = ["tenant_admin", "auditor", "super_admin"].includes(currentRole);

  const { layoutMode } = useSelector((state) => state.Layout || { layoutMode: "light" });
  const isDark = layoutMode === "dark";

  // State
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Metrics (Stat cards)
  const [stats, setStats] = useState({
    total: 0,
    revealsToday: 0,
    deniedToday: 0,
    failedLoginsToday: 0,
  });

  // Filters
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [resourceTypeFilter, setResourceTypeFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Expanded rows state (keyed by log _id)
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());

  const toggleRowExpanded = (id) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const fetchLogs = useCallback(
    async (targetPage = page) => {
      if (!isAuthorized) return;
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append("page", String(targetPage));
      params.append("limit", String(limit));

      if (search.trim()) params.append("search", search.trim());
      if (actionFilter && actionFilter !== "all") params.append("action", actionFilter);
      if (roleFilter && roleFilter !== "all") params.append("role", roleFilter);
      if (resourceTypeFilter && resourceTypeFilter !== "all") params.append("resourceType", resourceTypeFilter);
      if (fromDate) params.append("from", fromDate);
      if (toDate) params.append("to", toDate);

      try {
        const resp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}audit/logs?${params.toString()}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await resp.json();
        if (resp.ok && data.status) {
          setLogs(data.logs || []);
          setTotalCount(data.total || 0);
          setTotalPages(data.totalPages || 1);
          setPage(data.page || targetPage);
          if (data.stats) {
            setStats(data.stats);
          }
        } else {
          setError(data.error || "Failed to load audit logs");
        }
      } catch (err) {
        setError(err.message || "Network error loading audit logs");
      } finally {
        setLoading(false);
      }
    },
    [isAuthorized, token, page, limit, search, actionFilter, roleFilter, resourceTypeFilter, fromDate, toDate]
  );

  useEffect(() => {
    if (isAuthorized) {
      fetchLogs(1);
    }
  }, [actionFilter, roleFilter, resourceTypeFilter, fromDate, toDate, limit]);

  // Handle Export CSV
  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append("format", "csv");
      if (search.trim()) params.append("search", search.trim());
      if (actionFilter && actionFilter !== "all") params.append("action", actionFilter);
      if (roleFilter && roleFilter !== "all") params.append("role", roleFilter);
      if (resourceTypeFilter && resourceTypeFilter !== "all") params.append("resourceType", resourceTypeFilter);
      if (fromDate) params.append("from", fromDate);
      if (toDate) params.append("to", toDate);

      const resp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}audit/logs/export?${params.toString()}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!resp.ok) {
        const errJson = await resp.json().catch(() => ({}));
        throw new Error(errJson.error || "Failed to export audit logs");
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      a.download = `guardlogix_audit_logs_${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // Refresh to reflect the new audit:export log entry
      setTimeout(() => fetchLogs(1), 500);
    } catch (err) {
      setError(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setActionFilter("all");
    setRoleFilter("all");
    setResourceTypeFilter("all");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  if (!isAuthorized) {
    return <Redirect to="/dashboard" />;
  }

  // Action badge renderer
  const renderActionBadge = (action) => {
    const act = String(action || "").toLowerCase();
    const isDeniedOrFailed =
      act.includes("_denied") || act.includes("failed") || act.includes("kill") || act.includes("revoke") ||
      act.includes("delete") || act.includes("disabled") || act.endsWith(":mfa_reset") || act === "mfa:reset";
    const isRevealOrExport = act.includes("reveal") || act.includes("export");

    let bg, text, border, iconName;
    if (isDeniedOrFailed) {
      bg = isDark ? "rgba(239, 68, 68, 0.16)" : "#fef2f2";
      text = isDark ? "#f87171" : "#dc2626";
      border = isDark ? "rgba(239, 68, 68, 0.3)" : "#fecaca";
      iconName = "alert-octagon";
    } else if (isRevealOrExport) {
      bg = isDark ? "rgba(245, 158, 11, 0.16)" : "#fffbeb";
      text = isDark ? "#fbbf24" : "#d97706";
      border = isDark ? "rgba(245, 158, 11, 0.3)" : "#fde68a";
      iconName = act.includes("reveal") ? "eye" : "download";
    } else {
      bg = isDark ? "rgba(59, 130, 246, 0.16)" : "#eff6ff";
      text = isDark ? "#60a5fa" : "#2563eb";
      border = isDark ? "rgba(59, 130, 246, 0.3)" : "#bfdbfe";
      iconName = act.includes("login") ? "log-in" : act.includes("user") ? "user" : "activity";
    }

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          padding: "4px 10px",
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: 600,
          background: bg,
          color: text,
          border: `1px solid ${border}`,
        }}
      >
        <FeatherIcon icon={iconName} size={12} />
        <span>{getActionFriendlyLabel(action)}</span>
      </span>
    );
  };

  // Role badge renderer
  const renderRoleBadge = (role) => {
    const r = String(role || "read_only").toLowerCase();
    let bg = isDark ? "rgba(100, 116, 139, 0.2)" : "#f1f5f9";
    let text = isDark ? "#cbd5e1" : "#475569";
    let border = isDark ? "rgba(100, 116, 139, 0.3)" : "#e2e8f0";

    if (r === "tenant_admin" || r === "super_admin") {
      bg = isDark ? "rgba(225, 29, 72, 0.15)" : "#ffe4e6";
      text = isDark ? "#fb7185" : "#e11d48";
      border = isDark ? "rgba(225, 29, 72, 0.3)" : "#fecdd3";
    } else if (r === "auditor") {
      bg = isDark ? "rgba(168, 85, 247, 0.15)" : "#f3e8ff";
      text = isDark ? "#c084fc" : "#7e22ce";
      border = isDark ? "rgba(168, 85, 247, 0.3)" : "#e9d5ff";
    } else if (r === "analyst") {
      bg = isDark ? "rgba(59, 130, 246, 0.15)" : "#eff6ff";
      text = isDark ? "#60a5fa" : "#1d4ed8";
      border = isDark ? "rgba(59, 130, 246, 0.3)" : "#bfdbfe";
    }

    return (
      <span
        style={{
          display: "inline-block",
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "11px",
          fontWeight: 700,
          background: bg,
          color: text,
          border: `1px solid ${border}`,
          textTransform: "uppercase",
          letterSpacing: "0.3px",
        }}
      >
        {r.replace("_", " ")}
      </span>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content" style={{ minHeight: "100vh" }}>
        <MetaTags>
          <title>Audit Logs | GuardLogix ASM</title>
        </MetaTags>

        <Container fluid>
          {/* Executive Page Header */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(168, 85, 247, 0.25) 0%, rgba(99, 102, 241, 0.25) 100%)"
                    : "linear-gradient(135deg, #f3e8ff 0%, #e0e7ff 100%)",
                  color: isDark ? "#c084fc" : "#6366f1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.4)" : "0 4px 12px rgba(99, 102, 241, 0.15)",
                }}
              >
                <FeatherIcon icon="file-text" size={24} />
              </div>
              <div>
                <h4 className="mb-1" style={{ fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a" }}>
                  Audit Logs
                </h4>
                <p className="mb-0 text-muted" style={{ fontSize: "13.5px" }}>
                  Append-only traceability trail for authentication, scope, scans, reveals, and data exports.
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <Button
                color="light"
                onClick={() => fetchLogs(page)}
                disabled={loading}
                className="d-flex align-items-center gap-2 shadow-sm"
                style={{
                  height: "38px",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "13px",
                  border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                  background: isDark ? "#1e293b" : "#ffffff",
                  color: isDark ? "#cbd5e1" : "#475569",
                }}
              >
                <FeatherIcon icon="refresh-cw" size={14} className={loading ? "spin" : ""} />
                <span>Refresh</span>
              </Button>

              <Button
                color="primary"
                onClick={handleExportCsv}
                disabled={exporting || loading}
                className="d-flex align-items-center gap-2 shadow-sm"
                style={{
                  height: "38px",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "13.5px",
                  background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.35)",
                }}
              >
                {exporting ? <Spinner size="sm" /> : <FeatherIcon icon="download" size={15} />}
                <span>Export CSV</span>
              </Button>
            </div>
          </div>

          {/* Alert messages */}
          {error && (
            <Alert color="danger" className="d-flex align-items-center py-2 mb-3 shadow-sm" style={{ borderRadius: "8px" }}>
              <FeatherIcon icon="alert-circle" size={16} className="me-2 flex-shrink-0" />
              <span style={{ fontSize: "13.5px" }}>{error}</span>
            </Alert>
          )}

          {/* Executive Stat Cards */}
          <Row className="g-3 mb-4">
            <Col xs={12} sm={6} lg={3}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "12px",
                  background: isDark ? "#111a2e" : "#ffffff",
                  border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                  boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.04)",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "10px",
                    background: isDark ? "rgba(59, 130, 246, 0.15)" : "#eff6ff",
                    color: isDark ? "#60a5fa" : "#2563eb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FeatherIcon icon="list" size={20} />
                </div>
                <div>
                  <div style={{ fontSize: "11.5px", fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
                    Total Filtered Events
                  </div>
                  <div style={{ fontSize: "22px", fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a", lineHeight: 1.2 }}>
                    {totalCount}
                  </div>
                </div>
              </div>
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "12px",
                  background: isDark ? "#111a2e" : "#ffffff",
                  border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                  boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.04)",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "10px",
                    background: isDark ? "rgba(245, 158, 11, 0.15)" : "#fffbeb",
                    color: isDark ? "#fbbf24" : "#d97706",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FeatherIcon icon="eye" size={20} />
                </div>
                <div>
                  <div style={{ fontSize: "11.5px", fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
                    Reveals Today
                  </div>
                  <div style={{ fontSize: "22px", fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a", lineHeight: 1.2 }}>
                    {stats.revealsToday}
                  </div>
                </div>
              </div>
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "12px",
                  background: isDark ? "#111a2e" : "#ffffff",
                  border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                  boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.04)",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "10px",
                    background: isDark ? "rgba(239, 68, 68, 0.15)" : "#fef2f2",
                    color: isDark ? "#f87171" : "#dc2626",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FeatherIcon icon="shield-off" size={20} />
                </div>
                <div>
                  <div style={{ fontSize: "11.5px", fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
                    Denied Attempts Today
                  </div>
                  <div style={{ fontSize: "22px", fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a", lineHeight: 1.2 }}>
                    {stats.deniedToday}
                  </div>
                </div>
              </div>
            </Col>

            <Col xs={12} sm={6} lg={3}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "12px",
                  background: isDark ? "#111a2e" : "#ffffff",
                  border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                  boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.04)",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "10px",
                    background: isDark ? "rgba(225, 29, 72, 0.15)" : "#fff1f2",
                    color: isDark ? "#fb7185" : "#e11d48",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FeatherIcon icon="lock" size={20} />
                </div>
                <div>
                  <div style={{ fontSize: "11.5px", fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
                    Failed Logins Today
                  </div>
                  <div style={{ fontSize: "22px", fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a", lineHeight: 1.2 }}>
                    {stats.failedLoginsToday}
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Main Card with Filter Bar & Table */}
          <Card
            style={{
              borderRadius: "12px",
              border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
              background: isDark ? "#111a2e" : "#ffffff",
              boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <CardBody className="p-4">
              {/* Filter Bar */}
              <Row className="g-2 mb-3 align-items-center">
                <Col xs={12} md={3}>
                  <div className="position-relative">
                    <Input
                      type="text"
                      placeholder="Search target, email, IP..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") fetchLogs(1);
                      }}
                      style={{
                        paddingLeft: "36px",
                        borderRadius: "8px",
                        height: "38px",
                        fontSize: "13px",
                        background: isDark ? "#1e293b" : "#ffffff",
                        borderColor: isDark ? "#334155" : "#cbd5e1",
                        color: isDark ? "#f8fafc" : "#0f172a",
                      }}
                    />
                    <FeatherIcon
                      icon="search"
                      size={15}
                      className="position-absolute"
                      style={{
                        top: "12px",
                        left: "12px",
                        color: isDark ? "#64748b" : "#94a3b8",
                      }}
                    />
                  </div>
                </Col>

                {/* Grouped Action Dropdown */}
                <Col xs={6} md={3}>
                  <Input
                    type="select"
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                    style={{
                      borderRadius: "8px",
                      height: "38px",
                      fontSize: "13px",
                      background: isDark ? "#1e293b" : "#ffffff",
                      borderColor: isDark ? "#334155" : "#cbd5e1",
                      color: isDark ? "#f8fafc" : "#0f172a",
                    }}
                  >
                    <option value="all">All Actions</option>
                    <optgroup label="Authentication">
                      <option value="auth:">All Auth Events</option>
                      <option value="auth:login_success">Login Succeeded</option>
                      <option value="auth:login_failed">Login Failed</option>
                      <option value="auth:logout">Logout</option>
                    </optgroup>
                    <optgroup label="User Administration">
                      <option value="user:">All User Events</option>
                      <option value="user:create">User Created</option>
                      <option value="user:update">User Updated</option>
                      <option value="user:reset-password">Password Reset</option>
                      <option value="user:change-password">Password Changed</option>
                    </optgroup>
                    <optgroup label="Credentials">
                      <option value="credentials:">All Credential Events</option>
                      <option value="credentials:reveal">Credential Revealed</option>
                      <option value="credentials:reveal_denied">Reveal Blocked</option>
                      <option value="credentials:list_view">Credentials Viewed</option>
                    </optgroup>
                    <optgroup label="Target Scope">
                      <option value="scope:">All Scope Events</option>
                      <option value="scope:create">Scope Authorized</option>
                      <option value="scope:revoke">Scope Revoked</option>
                    </optgroup>
                    <optgroup label="Scans">
                      <option value="scan:">All Scan Events</option>
                      <option value="scan:trigger">Scan Triggered</option>
                      <option value="scan:kill">Scan Aborted</option>
                      <option value="scan:retest">Finding Retest</option>
                      <option value="scan:rescan">Scope Rescan</option>
                    </optgroup>
                    <optgroup label="Findings">
                      <option value="finding:">All Finding Events</option>
                      <option value="finding:status-change">Status Updated (Black Box)</option>
                      <option value="finding:status_update">Status Updated</option>
                      <option value="finding:assign">Finding Assigned</option>
                      <option value="finding:owner_update">Owner Changed</option>
                      <option value="finding:due_date_extended">Due Date Extended</option>
                      <option value="finding:risk_accepted">Risk Accepted</option>
                    </optgroup>
                    <optgroup label="Assets">
                      <option value="asset:">All Asset Events</option>
                      <option value="asset:add">Asset Added</option>
                      <option value="asset:update">Asset Edited</option>
                      <option value="asset:delete">Asset Deleted</option>
                    </optgroup>
                    <optgroup label="Account Security">
                      <option value="account:">All Account Events</option>
                      <option value="account:password_reset_request">Password Reset Requested</option>
                      <option value="account:password_reset_done">Password Reset Completed</option>
                      <option value="account:password_change">Password Changed (Profile)</option>
                    </optgroup>
                    <optgroup label="Two-Step Verification">
                      <option value="mfa:">All 2-Step Events</option>
                      <option value="mfa:enabled">Enabled</option>
                      <option value="mfa:disabled">Disabled</option>
                      <option value="mfa:login_failed">Code Failed</option>
                      <option value="mfa:reset">Reset</option>
                    </optgroup>
                    <optgroup label="SLA">
                      <option value="sla:">All SLA Events</option>
                    </optgroup>
                    <optgroup label="Brand Protection">
                      <option value="brand:">All Brand Events</option>
                    </optgroup>
                    <optgroup label="Certificates">
                      <option value="certificate:">All Certificate Events</option>
                    </optgroup>
                    <optgroup label="Red Team">
                      <option value="redteam:">All Red Team Events</option>
                    </optgroup>
                    <optgroup label="GuardLogix Admin Changes">
                      <option value="admin:">Changes by GuardLogix Admin</option>
                    </optgroup>
                    <optgroup label="Exports & Reports">
                      <option value="export:asset_inventory">Asset Inventory Export</option>
                      <option value="report:generate">ASM Report Generated</option>
                      <option value="report:blackbox_download">Scan Report Downloaded</option>
                      <option value="audit:export">Audit Logs Exported</option>
                    </optgroup>
                  </Input>
                </Col>

                {/* Role Dropdown */}
                <Col xs={6} md={2}>
                  <Input
                    type="select"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    style={{
                      borderRadius: "8px",
                      height: "38px",
                      fontSize: "13px",
                      background: isDark ? "#1e293b" : "#ffffff",
                      borderColor: isDark ? "#334155" : "#cbd5e1",
                      color: isDark ? "#f8fafc" : "#0f172a",
                    }}
                  >
                    <option value="all">All Roles</option>
                    <option value="tenant_admin">Admin</option>
                    <option value="auditor">Auditor</option>
                    <option value="analyst">Analyst</option>
                    <option value="read_only">Read Only</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="system">System (scheduled)</option>
                    <option value="admin">GuardLogix Admin</option>
                    <option value="unverified">Not signed in</option>
                  </Input>
                </Col>

                {/* Date range From */}
                <Col xs={6} md={2}>
                  <Input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    title="From Date"
                    style={{
                      borderRadius: "8px",
                      height: "38px",
                      fontSize: "12px",
                      background: isDark ? "#1e293b" : "#ffffff",
                      borderColor: isDark ? "#334155" : "#cbd5e1",
                      color: isDark ? "#f8fafc" : "#0f172a",
                    }}
                  />
                </Col>

                {/* Date range To */}
                <Col xs={6} md={2}>
                  <div className="d-flex align-items-center gap-1">
                    <Input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      title="To Date"
                      style={{
                        borderRadius: "8px",
                        height: "38px",
                        fontSize: "12px",
                        background: isDark ? "#1e293b" : "#ffffff",
                        borderColor: isDark ? "#334155" : "#cbd5e1",
                        color: isDark ? "#f8fafc" : "#0f172a",
                      }}
                    />
                    {(search || actionFilter !== "all" || roleFilter !== "all" || fromDate || toDate) && (
                      <Button
                        color="light"
                        onClick={resetFilters}
                        title="Reset Filters"
                        style={{
                          height: "38px",
                          borderRadius: "8px",
                          padding: "0 10px",
                          background: isDark ? "#1e293b" : "#f1f5f9",
                          border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
                          color: isDark ? "#94a3b8" : "#64748b",
                        }}
                      >
                        <FeatherIcon icon="x" size={14} />
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Table */}
              <div className="table-responsive" style={{ borderRadius: "8px" }}>
                <table
                  className="table align-middle mb-0"
                  style={{
                    color: isDark ? "#cbd5e1" : "#334155",
                    borderColor: isDark ? "#1e293b" : "#f1f5f9",
                  }}
                >
                  <thead
                    style={{
                      background: isDark ? "#172339" : "#f8fafc",
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: isDark ? "#94a3b8" : "#64748b",
                      borderBottom: `2px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                    }}
                  >
                    <tr>
                      <th style={{ width: "190px", padding: "12px 14px" }}>Timestamp</th>
                      <th style={{ width: "230px", padding: "12px 14px" }}>User</th>
                      <th style={{ width: "190px", padding: "12px 14px" }}>Action</th>
                      <th style={{ padding: "12px 14px" }}>Target</th>
                      <th style={{ width: "130px", padding: "12px 14px" }}>IP Address</th>
                      <th style={{ width: "100px", padding: "12px 14px", textAlign: "center" }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          <Spinner color="primary" />
                          <div className="mt-2 text-muted" style={{ fontSize: "13px" }}>
                            Loading audit trail...
                          </div>
                        </td>
                      </tr>
                    ) : logs.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          <div
                            style={{
                              width: 56,
                              height: 56,
                              borderRadius: "50%",
                              background: isDark ? "rgba(100, 116, 139, 0.15)" : "#f1f5f9",
                              color: isDark ? "#64748b" : "#94a3b8",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginBottom: "12px",
                            }}
                          >
                            <FeatherIcon icon="inbox" size={26} />
                          </div>
                          <div style={{ fontWeight: 600, fontSize: "15px", color: isDark ? "#f8fafc" : "#0f172a" }}>
                            No audit events found
                          </div>
                          <div className="text-muted mt-1" style={{ fontSize: "13px" }}>
                            Try adjusting your filters or date range to view logged activity.
                          </div>
                        </td>
                      </tr>
                    ) : (
                      logs.map((log) => {
                        const isExpanded = expandedRowIds.has(log._id);
                        const hasMetadata = log.metadata && Object.keys(log.metadata).length > 0;

                        return (
                          <React.Fragment key={log._id}>
                            <tr
                              style={{
                                background: isExpanded
                                  ? isDark
                                    ? "rgba(30, 41, 59, 0.4)"
                                    : "rgba(241, 245, 249, 0.6)"
                                  : "transparent",
                                transition: "background 0.15s ease",
                              }}
                            >
                              {/* Timestamp */}
                              <td style={{ padding: "12px 14px" }}>
                                <div style={{ fontWeight: 600, fontSize: "12.5px", color: isDark ? "#f1f5f9" : "#0f172a" }}>
                                  {formatExactTime(log.createdAt || log.timestamp)}
                                </div>
                                <div style={{ fontSize: "11px", color: isDark ? "#64748b" : "#94a3b8" }}>
                                  {formatRelativeTime(log.createdAt || log.timestamp)}
                                </div>
                              </td>

                              {/* User */}
                              <td style={{ padding: "12px 14px" }}>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: isDark ? "#f8fafc" : "#1e293b",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: "220px",
                                  }}
                                  title={log.who}
                                >
                                  {log.who}
                                </div>
                                <div className="mt-1">{renderRoleBadge(log.role)}</div>
                              </td>

                              {/* Action */}
                              <td style={{ padding: "12px 14px" }}>{renderActionBadge(log.action)}</td>

                              {/* Target */}
                              <td style={{ padding: "12px 14px" }}>
                                {log.target ? (
                                  <div
                                    style={{
                                      fontFamily: "monospace",
                                      fontSize: "12px",
                                      color: isDark ? "#e2e8f0" : "#334155",
                                      background: isDark ? "rgba(15, 23, 42, 0.5)" : "#f8fafc",
                                      padding: "3px 8px",
                                      borderRadius: "4px",
                                      display: "inline-block",
                                      maxWidth: "280px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                                    }}
                                    title={log.target}
                                  >
                                    {log.target}
                                  </div>
                                ) : (
                                  <span style={{ color: isDark ? "#64748b" : "#94a3b8", fontSize: "12px" }}>-</span>
                                )}
                                {log.resourceType && (
                                  <div style={{ fontSize: "11px", color: isDark ? "#64748b" : "#94a3b8", marginTop: "2px" }}>
                                    Type: {log.resourceType}
                                  </div>
                                )}
                              </td>

                              {/* IP */}
                              <td style={{ padding: "12px 14px" }}>
                                <span
                                  style={{
                                    fontFamily: "monospace",
                                    fontSize: "12px",
                                    color: isDark ? "#94a3b8" : "#64748b",
                                  }}
                                >
                                  {log.ip || "unknown"}
                                </span>
                              </td>

                              {/* Details button */}
                              <td style={{ padding: "12px 14px", textAlign: "center" }}>
                                <Button
                                  color="link"
                                  size="sm"
                                  disabled={!hasMetadata}
                                  onClick={() => toggleRowExpanded(log._id)}
                                  className="p-0 text-decoration-none"
                                  style={{
                                    color: hasMetadata
                                      ? isDark
                                        ? "#60a5fa"
                                        : "#2563eb"
                                      : isDark
                                      ? "#475569"
                                      : "#cbd5e1",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                  }}
                                >
                                  <div className="d-flex align-items-center justify-content-center gap-1">
                                    <span>{isExpanded ? "Hide" : "View"}</span>
                                    <FeatherIcon
                                      icon={isExpanded ? "chevron-up" : "chevron-down"}
                                      size={14}
                                    />
                                  </div>
                                </Button>
                              </td>
                            </tr>

                            {/* Expanded Details Row */}
                            {isExpanded && (
                              <tr
                                style={{
                                  background: isDark ? "#0f172a" : "#f8fafc",
                                  borderTop: "none",
                                }}
                              >
                                <td colSpan="6" style={{ padding: "14px 20px" }}>
                                  <div
                                    style={{
                                      padding: "12px 16px",
                                      borderRadius: "8px",
                                      background: isDark ? "#172339" : "#ffffff",
                                      border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        color: isDark ? "#94a3b8" : "#64748b",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      Event Metadata & Details
                                    </div>
                                    <div className="row g-2">
                                      {Object.entries(log.metadata || {}).map(([k, v]) => (
                                        <div key={k} className="col-12 col-md-6 col-lg-4">
                                          <div
                                            style={{
                                              padding: "6px 10px",
                                              borderRadius: "6px",
                                              background: isDark ? "rgba(15, 23, 42, 0.4)" : "#f1f5f9",
                                              fontSize: "12px",
                                            }}
                                          >
                                            <span style={{ fontWeight: 600, color: isDark ? "#cbd5e1" : "#475569" }}>
                                              {k}:{" "}
                                            </span>
                                            <span style={{ fontFamily: "monospace", color: isDark ? "#38bdf8" : "#0284c7" }}>
                                              {typeof v === "object" ? JSON.stringify(v) : String(v)}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Server-side Pagination footer */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-4 pt-3 border-top" style={{ borderColor: isDark ? "#1e293b" : "#f1f5f9" }}>
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: "13px", color: isDark ? "#94a3b8" : "#64748b" }}>
                    Showing {(page - 1) * limit + (logs.length > 0 ? 1 : 0)} to{" "}
                    {Math.min(page * limit, totalCount)} of {totalCount} events
                  </span>
                  <Input
                    type="select"
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                    style={{
                      width: "80px",
                      height: "32px",
                      fontSize: "12px",
                      padding: "2px 8px",
                      borderRadius: "6px",
                      background: isDark ? "#1e293b" : "#ffffff",
                      borderColor: isDark ? "#334155" : "#cbd5e1",
                      color: isDark ? "#f8fafc" : "#0f172a",
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Input>
                </div>

                <div className="d-flex align-items-center gap-1">
                  <Button
                    color="light"
                    size="sm"
                    disabled={page <= 1 || loading}
                    onClick={() => fetchLogs(page - 1)}
                    style={{
                      borderRadius: "6px",
                      padding: "4px 10px",
                      fontWeight: 600,
                      fontSize: "12.5px",
                      background: isDark ? "#1e293b" : "#ffffff",
                      borderColor: isDark ? "#334155" : "#cbd5e1",
                      color: isDark ? "#cbd5e1" : "#475569",
                    }}
                  >
                    <FeatherIcon icon="chevron-left" size={14} className="me-1" />
                    Previous
                  </Button>

                  <span
                    style={{
                      padding: "4px 10px",
                      fontSize: "12.5px",
                      fontWeight: 600,
                      color: isDark ? "#f8fafc" : "#0f172a",
                    }}
                  >
                    Page {page} of {totalPages}
                  </span>

                  <Button
                    color="light"
                    size="sm"
                    disabled={page >= totalPages || loading}
                    onClick={() => fetchLogs(page + 1)}
                    style={{
                      borderRadius: "6px",
                      padding: "4px 10px",
                      fontWeight: 600,
                      fontSize: "12.5px",
                      background: isDark ? "#1e293b" : "#ffffff",
                      borderColor: isDark ? "#334155" : "#cbd5e1",
                      color: isDark ? "#cbd5e1" : "#475569",
                    }}
                  >
                    Next
                    <FeatherIcon icon="chevron-right" size={14} className="ms-1" />
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AuditLogsManagement;
